/**
 * Die Symbole der Navigation.
 *
 * Sie tragen **keine Bedeutung allein**: In der Leiste steht neben jedem Symbol
 * seine Beschriftung — beim aktiven Reiter sichtbar, bei den übrigen für
 * Vorleseprogramme. Ein Symbol ohne Namen ist ein Rätsel, und „Fortschritt" ist
 * als Piktogramm ohnehin nicht eindeutig zu zeichnen.
 *
 * Gezeichnet wird mit `currentColor`, damit die Farbe aus dem Zustand des
 * Knopfes kommt und nicht hier noch einmal entschieden wird.
 */

const base = {
  width: 22,
  height: 22,
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 1.8,
  strokeLinecap: 'round',
  strokeLinejoin: 'round',
  'aria-hidden': true,
  focusable: false,
} as const;

export function IconToday(): React.JSX.Element {
  return (
    <svg {...base}>
      <circle cx="12" cy="12" r="8.5" />
      <path d="M12 7.5V12l3 1.8" />
    </svg>
  );
}

export function IconShopping(): React.JSX.Element {
  return (
    <svg {...base}>
      <path d="M3.5 4.5h2.2l2 10.5h10.6l1.9-7.5H7" />
      <circle cx="9.5" cy="19" r="1.3" />
      <circle cx="17" cy="19" r="1.3" />
    </svg>
  );
}

/** Haus — Reiter „Haushalt“ (Einkauf, Vorrat, Kosten, Mitglieder). */
export function IconHousehold(): React.JSX.Element {
  return (
    <svg {...base}>
      <path d="M4 11.2 12 4.5l8 6.7" />
      <path d="M6 9.8V19.5h12V9.8" />
      <path d="M10 19.5v-5h4v5" />
    </svg>
  );
}

export function IconTraining(): React.JSX.Element {
  return (
    <svg {...base}>
      <path d="M4 9v6M7 7v10M17 7v10M20 9v6M7 12h10" />
    </svg>
  );
}

export function IconNutrition(): React.JSX.Element {
  return (
    <svg {...base}>
      <path d="M6 3v8a2.5 2.5 0 0 0 5 0V3M8.5 11v10M17 3c-1.7 1.5-2 4-2 6.5s.6 3.5 2 3.5V3ZM17 13v8" />
    </svg>
  );
}

export function IconJournal(): React.JSX.Element {
  return (
    <svg {...base}>
      <path d="M6 3.5h11a1.5 1.5 0 0 1 1.5 1.5v14a1.5 1.5 0 0 1-1.5 1.5H6z" />
      <path d="M6 3.5v17M9.5 8h5.5M9.5 11.5h5.5" />
    </svg>
  );
}

/** Planen: ein Kalenderblatt mit Haken — Termine und Aufgaben. */
export function IconPlan(): React.JSX.Element {
  return (
    <svg {...base}>
      <path d="M4.5 6.5h15v13h-15z" />
      <path d="M4.5 10h15M8.5 4v4M15.5 4v4M9 14.5l2 2 4-4" />
    </svg>
  );
}

/**
 * Einstellungen: drei Regler. Bewusst kein Zahnrad — in dieser Größe sah es aus
 * wie eine Sonne und damit wie die Hell/Dunkel-Umschaltung.
 */
export function IconSettings(): React.JSX.Element {
  return (
    <svg {...base}>
      <path d="M4 6h9M17 6h3M4 12h3M11 12h9M4 18h11M19 18h1" />
      <circle cx="15" cy="6" r="2" />
      <circle cx="9" cy="12" r="2" />
      <circle cx="17" cy="18" r="2" />
    </svg>
  );
}

export function IconEye(): React.JSX.Element {
  return (
    <svg {...base}>
      <path d="M2.5 12S6 5.5 12 5.5 21.5 12 21.5 12 18 18.5 12 18.5 2.5 12 2.5 12Z" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  );
}

export function IconEyeOff(): React.JSX.Element {
  return (
    <svg {...base}>
      <path d="M2.5 12S6 5.5 12 5.5c2 0 3.7.7 5.1 1.7M21.5 12s-3.5 6.5-9.5 6.5c-2 0-3.7-.7-5.1-1.7" />
      <path d="M9.9 9.9a3 3 0 0 0 4.2 4.2M4 20 20 4" />
    </svg>
  );
}

/** Raum „Ich“ (27.10.2026): eine Person. */
export function IconMe(): React.JSX.Element {
  return (
    <svg {...base}>
      <circle cx="12" cy="8" r="3.5" />
      <path d="M5 20c.8-3.8 3.6-6 7-6s6.2 2.2 7 6" />
    </svg>
  );
}

/** Raum „Familie“: zwei Personen mit Herz — wer mithilft, ohne hier zu wohnen. */
export function IconFamily(): React.JSX.Element {
  return (
    <svg {...base}>
      <circle cx="8" cy="8" r="2.8" />
      <circle cx="16.5" cy="9" r="2.3" />
      <path d="M3 19.5c.6-3.2 2.6-5 5-5s4.4 1.8 5 5M14 14.6c2.6-.4 5.4 1.2 6 4.9" />
    </svg>
  );
}

/** Raum „Community“: ein Globus — Umgebung bis deutschlandweit. */
export function IconCommunity(): React.JSX.Element {
  return (
    <svg {...base}>
      <circle cx="12" cy="12" r="8.5" />
      <path d="M3.5 12h17M12 3.5c2.4 2.4 3.5 5.2 3.5 8.5s-1.1 6.1-3.5 8.5c-2.4-2.4-3.5-5.2-3.5-8.5s1.1-6.1 3.5-8.5Z" />
    </svg>
  );
}

/** Kosten bzw. Taschengeld: eine Münze. */
export function IconCosts(): React.JSX.Element {
  return (
    <svg {...base}>
      <circle cx="12" cy="12" r="8.5" />
      <path d="M14.8 8.6A3.6 3.6 0 0 0 9 10.8v2.4a3.6 3.6 0 0 0 5.8 2.2M7.5 11h5M7.5 13.5h5" />
    </svg>
  );
}

/** Mitglieder: drei Köpfe. */
export function IconMembers(): React.JSX.Element {
  return (
    <svg {...base}>
      <circle cx="12" cy="8" r="2.8" />
      <circle cx="5.5" cy="10" r="2.1" />
      <circle cx="18.5" cy="10" r="2.1" />
      <path d="M7 19.5c.6-3 2.5-4.8 5-4.8s4.4 1.8 5 4.8M2.5 18c.3-1.9 1.4-3.2 3-3.4M21.5 18c-.3-1.9-1.4-3.2-3-3.4" />
    </svg>
  );
}
